import { useEffect, useState } from "react";
import { Save, RefreshCw, CheckCircle2, ImageIcon, Type } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import BlocksEditor from "@/components/BlocksEditor";

const _simple = (label) => ({
  label,
  fields: {
    _root: {
      label: "Contenu",
      fields: [
        { key: "eyebrow", label: "Sur-titre", type: "text" },
        { key: "title", label: "Titre", type: "text" },
        { key: "banner_image", label: "URL de la bannière", type: "image" },
        { key: "intro", label: "Présentation", type: "textarea", rows: 6 },
      ],
    },
  },
});

const SCHEMAS = {
  home: {
    label: "Page d'accueil",
    fields: {
      hero: {
        label: "Bannière (Hero)",
        fields: [
          { key: "eyebrow", label: "Sur-titre (ex : Secrétariat Général à la Recherche)", type: "text" },
          { key: "title_left", label: "Titre — partie neutre", type: "text" },
          { key: "title_highlight", label: "Titre — partie mise en valeur (or)", type: "text" },
          { key: "subtitle", label: "Sous-titre principal", type: "textarea", rows: 3 },
          { key: "text_intro", label: "Texte d'introduction complémentaire", type: "textarea", rows: 2 },
          { key: "image_url", label: "URL de l'image de fond", type: "image" },
          { key: "cta_primary_label", label: "Bouton principal — libellé", type: "text" },
          { key: "cta_primary_link", label: "Bouton principal — lien", type: "text" },
          { key: "cta_secondary_label", label: "Bouton secondaire — libellé", type: "text" },
          { key: "cta_secondary_link", label: "Bouton secondaire — lien", type: "text" },
        ],
      },
      sg: {
        label: "Mot de la Rectrice",
        fields: [
          { key: "eyebrow", label: "Sur-titre (ex : Éditorial)", type: "text" },
          { key: "title", label: "Titre de la section", type: "text" },
          { key: "quote", label: "Citation (sans guillemets)", type: "textarea", rows: 5 },
          { key: "body", label: "Texte complémentaire", type: "textarea", rows: 4 },
          { key: "author_name", label: "Nom & titre de l'autrice", type: "text" },
          { key: "author_title", label: "Fonction (petit texte gris)", type: "text" },
          { key: "photo_url", label: "URL de la photo de la Rectrice", type: "image" },
        ],
      },
    },
  },
  "service.bibliotheque": {
    label: "Page Service des Bibliothèques et musées",
    fields: {
      _root: {
        label: "Contenu",
        fields: [
          { key: "eyebrow", label: "Sur-titre", type: "text" },
          { key: "title", label: "Titre de la page", type: "text" },
          { key: "banner_image", label: "URL de la bannière", type: "image" },
          { key: "intro", label: "Présentation (texte principal)", type: "textarea", rows: 8 },
          { key: "blocks", label: "Blocs de contenu détaillé (chaque bloc peut être « Texte normal » ou « Accordéon »)", type: "blocks" },
        ],
      },
    },
  },
  "service.bibliotheques_numeriques": {
    label: "Page Bibliothèques numériques",
    fields: {
      _root: {
        label: "Contenu",
        fields: [
          { key: "eyebrow", label: "Sur-titre", type: "text" },
          { key: "title", label: "Titre", type: "text" },
          { key: "banner_image", label: "URL de la bannière", type: "image" },
          { key: "intro", label: "Présentation", type: "textarea", rows: 6 },
        ],
      },
    },
  },
  "service.musees": {
    label: "Page Musées (hub)",
    fields: {
      _root: {
        label: "Contenu",
        fields: [
          { key: "eyebrow", label: "Sur-titre", type: "text" },
          { key: "title", label: "Titre", type: "text" },
          { key: "banner_image", label: "URL de la bannière", type: "image" },
          { key: "intro", label: "Présentation", type: "textarea", rows: 6 },
        ],
      },
    },
  },
  "service.musee.photos": {
    label: "Musée — Archives photos",
    fields: {
      _root: {
        label: "Contenu",
        fields: [
          { key: "eyebrow", label: "Sur-titre", type: "text" },
          { key: "title", label: "Titre", type: "text" },
          { key: "banner_image", label: "URL de la bannière", type: "image" },
          { key: "intro", label: "Présentation", type: "textarea", rows: 6 },
        ],
      },
    },
  },
  "service.musee.publications": {
    label: "Musée — Archives publications",
    fields: {
      _root: {
        label: "Contenu",
        fields: [
          { key: "eyebrow", label: "Sur-titre", type: "text" },
          { key: "title", label: "Titre", type: "text" },
          { key: "banner_image", label: "URL de la bannière", type: "image" },
          { key: "intro", label: "Présentation", type: "textarea", rows: 6 },
        ],
      },
    },
  },
  "service.musee.evenements": {
    label: "Musée — Archives Événements",
    fields: {
      _root: {
        label: "Contenu",
        fields: [
          { key: "eyebrow", label: "Sur-titre", type: "text" },
          { key: "title", label: "Titre", type: "text" },
          { key: "banner_image", label: "URL de la bannière", type: "image" },
          { key: "intro", label: "Présentation", type: "textarea", rows: 6 },
        ],
      },
    },
  },
  // Nouvelles pages Directions
  "directions.home": _simple("Page Directions (hub)"),
  "direction.bibliotheques_musees": _simple("Direction de la Recherche, des Bibliothèques et Musées (hub)"),
  "direction.suivi_encadrement": {
    label: "Direction Suivi et Encadrement",
    fields: {
      _root: {
        label: "Contenu",
        fields: [
          { key: "eyebrow", label: "Sur-titre", type: "text" },
          { key: "title", label: "Titre", type: "text" },
          { key: "banner_image", label: "URL de la bannière", type: "image" },
          { key: "intro", label: "Présentation", type: "textarea", rows: 6 },
          { key: "missions", label: "Missions (une par ligne, commencer par •)", type: "textarea", rows: 8 },
        ],
      },
    },
  },
  "pusic.home": _simple("PUSIC (hub)"),
  "pusic.publications": _simple("PUSIC — Publications universitaires (catalogue)"),
  "pusic.reproduction": {
    label: "PUSIC — Unité de Reproduction",
    fields: {
      _root: {
        label: "Contenu",
        fields: [
          { key: "eyebrow", label: "Sur-titre", type: "text" },
          { key: "title", label: "Titre", type: "text" },
          { key: "banner_image", label: "URL de la bannière", type: "image" },
          { key: "intro", label: "Présentation courte", type: "textarea", rows: 4 },
          { key: "presentation", label: "Présentation détaillée", type: "textarea", rows: 10 },
        ],
      },
    },
  },
  "formation.home": {
    label: "Formation",
    fields: {
      _root: {
        label: "Contenu",
        fields: [
          { key: "eyebrow", label: "Sur-titre", type: "text" },
          { key: "title", label: "Titre", type: "text" },
          { key: "banner_image", label: "URL de la bannière", type: "image" },
          { key: "intro", label: "Présentation", type: "textarea", rows: 6 },
          { key: "programs", label: "Programmes (une par ligne, commencer par •)", type: "textarea", rows: 8 },
        ],
      },
    },
  },
  "bm.centrale": _simple("Bibliothèques et Musées (page centrale)"),
  "bm.bibliotheque_centrale": {
    label: "Bibliothèque centrale",
    fields: {
      _root: {
        label: "Contenu",
        fields: [
          { key: "eyebrow", label: "Sur-titre", type: "text" },
          { key: "title", label: "Titre", type: "text" },
          { key: "banner_image", label: "URL de la bannière", type: "image" },
          { key: "intro", label: "Présentation", type: "textarea", rows: 6 },
          { key: "collections", label: "Collections (liste, une par ligne avec •)", type: "textarea", rows: 6 },
          { key: "hours", label: "Horaires (une ligne par jour)", type: "textarea", rows: 4 },
          { key: "address", label: "Adresse", type: "textarea", rows: 2 },
        ],
      },
    },
  },
  "bm.activites_animation": _simple("Activités d'animation scientifique"),
  "bm.musees_universitaires": _simple("Musées universitaires (hub)"),
  "bm.musee.phototheque": _simple("Musée — Photothèque"),
  "bm.musee.presse_ecrite": _simple("Musée — Presse écrite congolaise"),
  "bm.musee.audiovisuel": _simple("Musée — Audiovisuel congolais"),
  "bm.musee.cinema": _simple("Musée — Cinéma congolais"),
  "bm.musee.tic": _simple("Musée — TIC en RDC"),
  "service.ecole_doctorale": {
    label: "Page École Doctorale",
    fields: {
      _root: {
        label: "Contenu",
        fields: [
          { key: "eyebrow", label: "Sur-titre", type: "text" },
          { key: "title", label: "Titre", type: "text" },
          { key: "banner_image", label: "URL de la bannière", type: "image" },
          { key: "intro", label: "Présentation (texte principal)", type: "textarea", rows: 8 },
          { key: "blocks", label: "Blocs de contenu détaillé (chaque bloc peut être « Texte normal » ou « Accordéon »)", type: "blocks" },
        ],
      },
    },
  },
  "service.cecom": {
    label: "Page CECOM",
    fields: {
      _root: {
        label: "Contenu",
        fields: [
          { key: "eyebrow", label: "Sur-titre", type: "text" },
          { key: "title", label: "Titre", type: "text" },
          { key: "banner_image", label: "URL de la bannière", type: "image" },
          { key: "intro", label: "Présentation", type: "textarea", rows: 8 },
          { key: "blocks", label: "Blocs de contenu détaillé (chaque bloc peut être « Texte normal » ou « Accordéon »)", type: "blocks" },
          { key: "missions", label: "Missions (encadré à droite — une par ligne, commencer par •)", type: "textarea", rows: 6 },
          { key: "axes", label: "Axes de recherche (colonne principale — laissez vide pour masquer la section)", type: "textarea", rows: 6 },
          { key: "activites", label: "Activités scientifiques (colonne principale — laissez vide pour masquer)", type: "textarea", rows: 4 },
          { key: "cahiers_title", label: "Bloc 2 — Titre (Cahiers Congolais de Communication)", type: "text" },
          { key: "cahiers_content", label: "Bloc 2 — Texte de présentation", type: "textarea", rows: 5 },
          { key: "cahiers_external_url", label: "Bloc 2 — URL du site externe de la revue (facultatif)", type: "text" },
        ],
      },
    },
  },
  "service.sg": {
    label: "Page Secrétariat Général à la Recherche",
    fields: {
      _root: {
        label: "Contenu",
        fields: [
          { key: "eyebrow", label: "Sur-titre", type: "text" },
          { key: "title", label: "Titre", type: "text" },
          { key: "banner_image", label: "URL de la bannière", type: "image" },
          { key: "intro", label: "Présentation", type: "textarea", rows: 8 },
          { key: "blocks", label: "Blocs de contenu détaillé (chaque bloc peut être « Texte normal » ou « Accordéon »)", type: "blocks" },
        ],
      },
    },
  },
  "platform.depots": {
    label: "Page Dépôts des travaux académiques",
    fields: {
      _root: {
        label: "Bannière & introduction",
        fields: [
          { key: "banner_image", label: "URL de la bannière (image affichée en haut de la page)", type: "image" },
        ],
      },
    },
  },
};

function ImageUpload({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "content");
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
      onChange(url);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="flex gap-2 flex-wrap">
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="URL de l'image (https://...)"
          className="flex-1 min-w-[200px] border border-slate-300 px-3 py-2 rounded-sm text-sm"
        />
        <label className="btn-secondary text-xs cursor-pointer">
          <ImageIcon className="w-3.5 h-3.5" />
          {uploading ? "Envoi..." : "Téléverser"}
          <input type="file" accept="image/*" onChange={handle} className="hidden" />
        </label>
      </div>
      {value && (
        <div className="mt-2 border border-slate-200 rounded-sm overflow-hidden max-w-xs">
          <img src={value} alt="Aperçu" className="w-full h-32 object-cover" />
        </div>
      )}
      {err && <div className="mt-1 text-xs text-unisic-red">{err}</div>}
    </div>
  );
}

function FieldEditor({ field, value, onChange, data }) {
  if (field.type === "image") {
    return <ImageUpload value={value} onChange={onChange} />;
  }
  if (field.type === "blocks") {
    return (
      <BlocksEditor
        value={value}
        legacyBody={typeof data?.body === "string" ? data.body : ""}
        onChange={onChange}
      />
    );
  }
  if (field.type === "textarea") {
    return (
      <textarea
        rows={field.rows || 3}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-300 px-3 py-2 rounded-sm text-sm"
      />
    );
  }
  return (
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-slate-300 px-3 py-2 rounded-sm text-sm"
    />
  );
}

function ContentSection({ contentKey, schema }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get(`/content/${contentKey}`);
      setData(data || {});
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await api.put(`/content/${contentKey}`, { data });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const setField = (group, key, value) => {
    setData((p) => {
      const next = { ...(p || {}) };
      if (group === "_root") {
        next[key] = value;
      } else {
        next[group] = { ...(next[group] || {}), [key]: value };
      }
      return next;
    });
  };

  if (!data) return <div className="text-slate-500 text-sm">Chargement...</div>;

  return (
    <div data-testid={`content-editor-${contentKey}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow eyebrow-dark">Édition de contenu</div>
          <h2 className="mt-1 text-2xl font-bold text-unisic-blue">{schema.label}</h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={load}
            className="text-xs text-slate-500 hover:text-unisic-blue flex items-center gap-1"
            data-testid="btn-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Recharger
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="btn-primary text-sm"
            data-testid="btn-save-content"
          >
            <Save className="w-4 h-4" /> {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>
      <span className="divider-gold mt-3" />

      {saved && (
        <div className="mt-4 flex items-center gap-2 bg-green-50 border border-green-100 text-green-700 px-4 py-2 rounded-sm text-sm">
          <CheckCircle2 className="w-4 h-4" /> Modifications enregistrées et publiées.
        </div>
      )}
      {error && (
        <div className="mt-4 text-sm text-unisic-red bg-red-50 border border-red-100 px-4 py-2 rounded-sm">
          {error}
        </div>
      )}

      <div className="mt-6 space-y-6">
        {Object.entries(schema.fields).map(([group, def]) => (
          <div key={group} className="paper-card p-6">
            <div className="flex items-center gap-2">
              <Type className="w-4 h-4 text-unisic-gold" />
              <h3 className="font-display font-semibold text-unisic-blue">{def.label}</h3>
            </div>
            <div className="mt-5 grid gap-5">
              {def.fields.map((f) => {
                const value = group === "_root" ? data[f.key] : (data[group] || {})[f.key];
                return (
                  <div key={f.key}>
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">
                      {f.label}
                    </label>
                    <div className="mt-2">
                      <FieldEditor
                        field={f}
                        value={value}
                        data={data}
                        onChange={(v) => setField(group, f.key, v)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ContentEditor() {
  const keys = Object.keys(SCHEMAS);
  const [active, setActive] = useState(keys[0]);

  return (
    <div data-testid="content-editor-page">
      <div className="eyebrow eyebrow-dark">Éditeur de contenu</div>
      <h1 className="mt-2 text-3xl font-bold text-unisic-blue">Pages publiques</h1>
      <p className="mt-2 text-slate-600 text-sm">
        Modifiez librement les textes et images des pages publiques. Les changements sont
        immédiatement visibles sur le portail.
      </p>
      <span className="divider-gold mt-3" />

      <div className="mt-8 flex gap-2 flex-wrap" data-testid="content-tabs">
        {keys.map((k) => (
          <button
            key={k}
            onClick={() => setActive(k)}
            className={`px-4 py-2 text-sm rounded-sm border transition-colors ${
              active === k
                ? "bg-unisic-blue text-white border-unisic-blue"
                : "bg-white text-slate-700 border-slate-300 hover:border-unisic-gold"
            }`}
            data-testid={`content-tab-${k}`}
          >
            {SCHEMAS[k].label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        <ContentSection key={active} contentKey={active} schema={SCHEMAS[active]} />
      </div>
    </div>
  );
}
